import { useEffect, useState, useCallback } from 'react';
import {
    AdaptivityProvider,
    AppRoot,
    View,
    Panel,
    PanelHeader,
    PanelHeaderButton,
    ConfigProvider,
    useColorScheme,
} from '@vkontakte/vkui';
import { Icon28HelpOutline } from '@vkontakte/icons';
import { CssVarsProvider, useColorScheme as useJoyColorScheme } from '@mui/joy/styles';
import ApiWidget from './components/ApiWidget';
import '@vkontakte/vkui/dist/vkui.css';
import bridge from '@vkontakte/vk-bridge';
import ModalFAQ from './components/ModalFAQ';

const ANIM_DURATION = 280;

function AppContent() {
    const { colorScheme } = useColorScheme();
    const { setMode } = useJoyColorScheme();

    const [faqOpen, setFaqOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (colorScheme) setMode(colorScheme);
    }, [colorScheme, setMode]);

    const handleOpen = () => setFaqOpen(true);
    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setFaqOpen(false);
            setIsClosing(false);
        }, ANIM_DURATION);
    }, []);

    return (
        <>
            <AdaptivityProvider>
                <AppRoot>
                    <View activePanel="main">
                        <Panel id="main">
                            <PanelHeader
                                after={
                                    <PanelHeaderButton onClick={handleOpen}>
                                        <Icon28HelpOutline
                                            style={{ color: 'var(--vkui--color_icon_primary)' }}
                                        />
                                    </PanelHeaderButton>
                                }
                            >
                                Виджет «За Христа пострадавшие»
                            </PanelHeader>
                            <ApiWidget />
                        </Panel>
                    </View>
                </AppRoot>
            </AdaptivityProvider>

            <ModalFAQ
                open={faqOpen}
                onClose={handleClose}
                modalSx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '@keyframes backdropIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                    '@keyframes backdropOut': { from: { opacity: 1 }, to: { opacity: 0 } },
                    '& .MuiModal-backdrop': {
                        animation: isClosing
                            ? `backdropOut ${ANIM_DURATION}ms ease forwards`
                            : `backdropIn  ${ANIM_DURATION}ms ease forwards`,
                    },
                }}
                sheetSx={{
                    maxWidth: 480,
                    width: '90%',
                    borderRadius: '24px',
                    p: 3,
                    boxShadow: 'lg',
                    outline: 'none',
                    '@keyframes modalSpringIn': {
                        '0%': { opacity: 0, transform: 'scale(0.82) translateY(16px)' },
                        '60%': { opacity: 1, transform: 'scale(1.03) translateY(-3px)' },
                        '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                    },
                    '@keyframes modalSpringOut': {
                        '0%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                        '100%': { opacity: 0, transform: 'scale(0.88) translateY(12px)' },
                    },
                    animation: isClosing
                        ? `modalSpringOut ${ANIM_DURATION}ms cubic-bezier(0.4, 0, 1, 1) forwards`
                        : `modalSpringIn  ${ANIM_DURATION}ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
                }}
            />
        </>
    );
}

function App() {
    useEffect(() => {
        bridge.send('VKWebAppInit');
    }, []);

    return (
        <CssVarsProvider defaultMode="system">
            <ConfigProvider>
                <AppContent />
            </ConfigProvider>
        </CssVarsProvider>
    );
}

export default App;
